import { Link, useParams } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { assetUrl, useRecord } from '../app/useSiteData.js'
import { BlockRenderer } from '../blocks/index.js'
import { readingMinutes } from '../lib/blocks.js'
import { formatDate } from '../lib/format.js'
import { useI18n } from '../lib/i18n.jsx'
import { Chip, Container, EmptyState, Media, Prose, Row, Section } from '../ui/index.js'

/**
 * One news article.
 *
 * A document, not a landing page: breadcrumb, headline, the two facts a reader
 * checks before committing (when it was written, how long it takes), the cover
 * if there is one, then the body. The body is whatever the editor assembled in
 * blocks — this page hands it to `BlockRenderer` and makes no decisions about
 * it, which is the point of a block model.
 *
 * A deleted or unpublished article is not an error state. It answers 404, and
 * the honest response is a short explanation plus the way back to the list, not
 * a red alert box about a failed request.
 *
 * 单篇新闻。
 *
 * 这是一篇文档而不是落地页：面包屑、标题、读者在决定是否往下读之前会看的两个事实
 * （写于何时、需要读多久）、有则显示的封面，然后是正文。正文是编辑用块拼出来的内容
 * ——本页把它交给 BlockRenderer，不对它做任何决定，这正是块模型的意义。
 *
 * 已删除或未发布的文章不是错误状态。服务端答 404，诚实的回应是一句说明加一条回列表
 * 的路，而不是一个关于「请求失败」的红色警告框。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function NewsDetail() {
  const { id } = useParams()
  const { t, pick, lang } = useI18n()
  const { data: record, loading, error, reload } = useRecord('news', id)

  const missing = error && error.status === 404
  const cover = record ? assetUrl(record.cover) : ''
  const tags = Array.isArray(record?.tags) ? record.tags : []

  return (
    <Section className="news-detail">
      <Container width="narrow">
        <nav className="detail-breadcrumb" aria-label={t('news.title')}>
          <Link className="detail-breadcrumb__link" to="/news">
            {t('news.title')}
          </Link>
        </nav>

        <PageState
          loading={loading}
          error={missing ? null : error}
          onRetry={reload}
          isEmpty={missing || !record}
          empty={
            <EmptyState
              title={t('news.notFound')}
              body={t('error.notFoundBody')}
              action={
                <Link className="link-button link-button--ghost" to="/news">
                  {t('news.backToList')}
                </Link>
              }
            />
          }
        >
          {record && (
            <article className="news-article">
              <header className="news-article__head">
                <h1 className="news-article__title">{pick(record.title)}</h1>
                <Row className="news-article__meta" gap="tight">
                  <time className="news-article__date" dateTime={String(record.date || '').slice(0, 10)}>
                    {formatDate(record.date, lang)}
                  </time>
                  <span className="news-article__reading">
                    {t('news.readingTime', { n: readingMinutes(record.blocks, lang) })}
                  </span>
                </Row>
                {tags.length > 0 && (
                  <Row className="news-article__tags" gap="tight">
                    {tags.map((tag) => (
                      <Chip key={tag} className="news-article__tag">
                        {tag}
                      </Chip>
                    ))}
                  </Row>
                )}
              </header>

              {/* The cover carries the headline's meaning, so it is described by
                  the headline rather than given a second, competing description.
                  封面承载的是标题的含义，因此由标题来描述它，而不是再给一个互相竞争的
                  描述。 */}
              {cover && (
                <Media className="news-article__cover">
                  <img src={cover} alt={pick(record.title)} loading="lazy" decoding="async" />
                </Media>
              )}

              <Prose className="news-article__body">
                <BlockRenderer blocks={record.blocks} />
              </Prose>
            </article>
          )}
        </PageState>
      </Container>
    </Section>
  )
}
